import { FC, useEffect } from 'react';
import { Button, Flex, Loader, Stack, Text } from '@mantine/core';
import { IconCertificate } from '@tabler/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CardContainer } from '../../cardContainer';
import { ExpiredPayment } from '../../expiredPayment';
import { ValidPayment } from '../../validPayment';
import { useAppSelector } from '@/hooks/redux/redux';
import { ModuleUrl } from '@/entities/admin-app/auth';
import useHomeWidgetSlice from '@/store/homeWidget/useHomeWidgetSlice';

interface IPaymentSectionProps {
  subscriptionPage?: boolean;
  paymentsDate: string | null;
  filialId: number;
}

export const PaymentSection: FC<IPaymentSectionProps> = ({
  subscriptionPage,
  filialId,
  paymentsDate
}) => {
  const { t } = useTranslation();
  const [{ data, isLoading }] = useHomeWidgetSlice().usePaymentWidget().useState();
  const { LoadPaymentSection } = useHomeWidgetSlice().actions;

  const modules = useAppSelector(
    (state) => state.userInfo.Settings?.ModuleSettings?.ModuleUrls
  ) as ModuleUrl[];

  useEffect(() => {
    LoadPaymentSection({ id: filialId, paymentsDate });
  }, [paymentsDate]);

  const paymentModule = Array.isArray(modules)
    ? modules.find((module) => module.Name === 'Payments')
    : undefined;
  const PayLink = paymentModule?.Url;

  const isPayedTariff = () => data?.Tariff?.Type !== -1;

  const isExpiredPayment = () => {
    const tariffDate = data?.Date ? new Date(data.Date) : new Date();
    return tariffDate <= new Date();
  };

  const getUserCount = () => {
    if (!data?.UserCounter?.PossibleAmount) return 0;
    return `${data.UserCounter.CurrentAmount} / ${data.UserCounter.PossibleAmount}`;
  };

  const calculateDaysRemaining = (expiringDate?: string) => {
    if (!expiringDate) return 0;
    const currentDate = new Date();
    const futureDate = new Date(expiringDate);
    const timeDifference = futureDate.getTime() - currentDate.getTime();
    return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
  };

  const remainingDays = calculateDaysRemaining(data?.Date);
  const isExpired = isExpiredPayment();

  if (isLoading) {
    return (
      <CardContainer
        sectionName={t('filials.payments')}
        Icon={IconCertificate}
        subscriptionPage={subscriptionPage}
      >
        <Flex w="100%" align="center" justify="center">
          <Loader size="sm" />
        </Flex>
      </CardContainer>
    );
  }

  return (
    <CardContainer
      sectionName={t('filials.payments')}
      Icon={IconCertificate}
      subscriptionPage={subscriptionPage}
    >
      {isExpired && <ExpiredPayment usedSpace={data?.DiskCounter.CurrentAmount} link={PayLink} />}
      {!isExpired && (
        <>
          <ValidPayment name={data?.Name} remainingDays={remainingDays} />
          <Stack gap="4px">
            <Flex justify="space-between">
              <Text fz="sm" c="#848E98">
                {t('filials.diskSpace.validity')}
              </Text>
              <Text fz="sm" fw="500">
                {isPayedTariff()
                  ? data?.Date
                    ? `${t('filials.diskSpace.to')} ${new Date(data.Date).toLocaleDateString(
                        'ru-RU'
                      )}`
                    : ''
                  : t('filials.diskSpace.unlimited')}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Text fz="sm" c="#848E98">
                {t('filials.diskSpace.usersNumber')}
              </Text>
              <Text fz="sm" fw="500">
                {isPayedTariff() ? getUserCount() : data?.UserCounter?.CurrentAmount || 1}
              </Text>
            </Flex>
          </Stack>
        </>
      )}
      <Button
        w="100%"
        mt="16px"
        fw="500"
        component={Link}
        target={!subscriptionPage ? '_blank' : '_self'}
        to={PayLink ?? '#'}
        radius="4px"
      >
        {remainingDays > 30 ? t('change') : t('filials.diskSpace.activateTariff')}
      </Button>
    </CardContainer>
  );
};
